/**
 * useVoiceEvents — connect to /ws/voice-events, receive call lifecycle events,
 * maintain a local list of active calls. Falls back to the existing
 * /api/voice-ai/active-calls REST endpoint on initial load and on reconnect.
 *
 * Design choice: on every call.ringing / call.ended event we rehydrate from
 * /active-calls rather than merging optimistically. Slightly more HTTP traffic
 * (≤10 active calls × 1 GET = trivial) but the source-of-truth payload shape
 * never drifts.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { apiRequest } from '@/lib/queryClient';

export interface ActiveCall {
  id: number;
  callId: string;
  callerNumber: string;
  callerName: string | null;
  callerType: string | null;
  language: string | null;
  startedAt: string;
  elapsedSeconds: number;
  clientId: number | null;
  clientName: string | null;
  clientUsername: string | null;
  weddingId: number | null;
  brideName: string | null;
  groomName: string | null;
  weddingDate: string | null;
  weddingTitle: string | null;
  timeSlot: string | null;
  guestCount: number | null;
  weddingStatus: string | null;
  venue: string | null;
  balanceAmount: string | number | null;
  stage: 'lead' | 'quoted' | 'booked' | 'event-week' | 'past_client' | 'unknown' | null;
}

interface VoiceEvent {
  type: 'hello' | 'call.ringing' | 'call.answered' | 'call.ended';
  [k: string]: any;
}

export function useVoiceEvents(): { calls: ActiveCall[]; connected: boolean; enabled: boolean } {
  const [calls, setCalls] = useState<ActiveCall[]>([]);
  const [connected, setConnected] = useState(false);
  // server-side flag returned by /active-calls; defaults to on while hydrating
  const [enabled, setEnabled] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(1000);
  const closedRef = useRef(false);
  const failuresRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hydrate = useCallback(async () => {
    try {
      const res = await apiRequest('/api/voice-ai/active-calls', { method: 'GET' });
      const data = await res.json();
      setCalls(data.calls || []);
      // Server flag — defaults to true if missing (older deployments without P2 field).
      setEnabled(data.enabled !== false);
    } catch {
      // ignore; will retry next reconnect
    }
  }, []);

  const connect = useCallback(() => {
    if (closedRef.current) return;
    // Clear any fired/pending reconnect handle so the `!reconnectTimerRef.current`
    // guards in revive()/watchdog reflect reality. (Review 2026-07-12.)
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    const token = localStorage.getItem('token');
    if (!token) return;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws/voice-events?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    // Per-connection opened flag to distinguish auth failures from normal disconnects
    let opened = false;

    ws.onopen = () => {
      opened = true;
      setConnected(true);
      reconnectDelayRef.current = 1000;
      failuresRef.current = 0;
      reconnectTimerRef.current = null;
      hydrate(); // grab current state on (re)connect
    };
    ws.onclose = () => {
      setConnected(false);
      if (closedRef.current) return;

      // If the connection never opened, it's likely an auth failure (expired token, etc.).
      // Track consecutive failures and cap reconnects.
      if (!opened) {
        failuresRef.current += 1;
        if (failuresRef.current >= 8) {
          console.warn('[useVoiceEvents] giving up after repeated WS failures — token may be expired; will retry on next mount');
          return;
        }
      } else {
        // Connection had successfully opened before closing. Reset backoff and reconnect promptly.
        failuresRef.current = 0;
        reconnectDelayRef.current = 1000;
      }

      const delay = Math.min(reconnectDelayRef.current, 30_000);
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30_000);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
    ws.onerror = () => {
      try { ws.close(); } catch { /* ignore */ }
    };
    ws.onmessage = (m) => {
      let ev: VoiceEvent;
      try { ev = JSON.parse(m.data); } catch { return; }
      if (ev.type === 'call.ringing' || ev.type === 'call.ended') {
        // Rehydrate from REST to get the full row shape — never merge optimistically.
        hydrate();
      }
    };
  }, [hydrate]);

  useEffect(() => {
    closedRef.current = false;
    connect();

    // Resilience: a half-open WS (laptop sleep/wake, wifi flap, LB idle-timeout
    // with no FIN) can leave readyState OPEN while silently receiving nothing —
    // the popup then only updates on a manual refresh. Recover proactively:
    //  - when the tab becomes visible / window regains focus / network returns,
    //    reconnect if the socket is dead, else re-hydrate to catch missed events;
    //  - a watchdog reconnects if the socket is closed and no reconnect is pending.
    const revive = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (!reconnectTimerRef.current) connect();
      } else if (ws.readyState === WebSocket.OPEN) {
        hydrate();
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') revive(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', revive);
    window.addEventListener('online', revive);
    const watchdog = setInterval(() => {
      const ws = wsRef.current;
      if ((!ws || ws.readyState === WebSocket.CLOSED) && !reconnectTimerRef.current && !closedRef.current) {
        connect();
      }
    }, 20_000);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', revive);
      window.removeEventListener('online', revive);
      clearInterval(watchdog);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
    };
  }, [connect, hydrate]);

  // While a card is on screen, re-check every 15 s. The card closes on the
  // call.ended push, but a push lost to a half-open socket (or a hangup whose
  // event never fired) must not leave it up — the server is the source of
  // truth for "still live". No polling at all while nothing is showing.
  const hasCalls = calls.length > 0;
  useEffect(() => {
    if (!hasCalls) return;
    const t = setInterval(() => { hydrate(); }, 15_000);
    return () => clearInterval(t);
  }, [hasCalls, hydrate]);

  return { calls, connected, enabled };
}
